/**
 * User name utilities - centralized functions for consistent name handling
 */

export interface UserNameData {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

/**
 * Build a full display name from first and last name
 */
export function buildDisplayName(userData: UserNameData | null | undefined): string {
  const { first_name, last_name, email } = userData || {};
  
  // First try full name from first_name + last_name
  if (first_name && last_name) {
    return `${first_name} ${last_name}`;
  }
  
  if (first_name) {
    return first_name;
  }
  
  if (last_name) {
    return last_name;
  }
  
  // display_name removed - use first_name/last_name or email fallback
  
  if (email) {
    return email.split('@')[0];
  }
  
  return 'User';
}

/**
 * Get just the first name for personalized greetings
 */
export function getFirstName(userData: UserNameData | null | undefined): string {
  const { first_name, email } = userData || {};
  
  if (first_name) {
    return first_name;
  }
  
  if (email) {
    return email.split('@')[0];
  }
  
  return 'User';
}

/**
 * Get initials for avatar fallbacks
 */
export function getInitials(userData: UserNameData | null | undefined, singleInitial = false): string {
  const { first_name, last_name, email } = userData || {};
  
  if (singleInitial) {
    // Prioritize last name's first letter if available
    if (last_name) {
      return last_name.charAt(0).toUpperCase();
    }
    if (first_name) {
      return first_name.charAt(0).toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  }
  
  // Two initials
  if (first_name && last_name) {
    return `${first_name.charAt(0)}${last_name.charAt(0)}`.toUpperCase();
  }
  
  if (first_name) {
    return first_name.charAt(0).toUpperCase();
  }
  
  if (email) {
    return email.charAt(0).toUpperCase();
  }
  
  return 'U';
}

/**
 * Capitalize first letter of a name
 */
export function capitalizeName(name: string): string {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}